import React, { useEffect, useState } from "react";
import { Table, Input, Tag, Space, Popconfirm, DatePicker } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../assets/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/delete-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteProject,
  fetchAllProjects,
  setProjectDetailsIsEdit,
} from "./redux/actions";
import SpinnerComponent from "../../components/spinner";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import CustomPagination from "../../components/customPagination/CustomPagination";
import moment from "moment";
import CustomCalendar from "../../components/calendar";
import AMultiSelect from "../../components/AMultiSelect";

const ProjectsRegisterTable = ({ canUpdate, canDelete }) => {
  const [sortedInfo, setSortedInfo] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [currentDelRecord, setCurrentDelRecord] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(() => 1);
  const [resultPerPage, setResultPerPage] = useState(() => 10);
  const [req, setReq] = useState(false);
  const [search, setSearch] = useState({});
  const [defaultPageOneReq, setDefaultPageOneReq] = useState(false);
  const [statusCheckedList, setStatusCheckedList] = useState([]);
  const [datePickerOpened, setDatePickerOpened] = useState(false);
  const [date, setDate] = useState(null);
  const [projectStatuses, SetProjectStatuses] = useState([]);

  const {
    allProjects,
    fetchProjectsLoading,
    deleteProjectLoading,
    totalRecords,
  } = useSelector((state) => state.projectsSetupReducer);

  const { allProjectStatuses } = useSelector(
    (state) => state.projectStatusReducer
  );

  useEffect(() => {
    let updatedStatused = allProjectStatuses.map((el) => {
      const { id, statusName } = el;
      return { value: id, label: statusName };
    });
    SetProjectStatuses(updatedStatused);
  }, [allProjectStatuses]);

  useEffect(() => {
    if (page && resultPerPage && page > 0 && resultPerPage > 0) {
      dispatch(
        fetchAllProjects({
          page: page,
          perPage: resultPerPage,
          search,
          sort: sortedInfo?.order && sortedInfo?.columnKey,
          order: sortedInfo?.order
            ? sortedInfo?.order === "ascend"
              ? "asc"
              : "desc"
            : null,
          showByDefault: true,
        })
      );
    }
  }, [req]);

  useEffect(() => {
    let paramsPage = 1;
    if (paramsPage && resultPerPage && paramsPage > 0 && resultPerPage > 0) {
      dispatch(
        fetchAllProjects({
          page: paramsPage,
          perPage: resultPerPage,
          search,
          showByDefault: true,
        })
      );
      setPage(1);
    }
  }, [defaultPageOneReq]);

  useEffect(() => {
    debouncedCallApi();
  }, [sortedInfo]);

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 1000)
  );

  const [debouncedDefaultPageOneCallApi] = useState(() =>
    _.debounce(() => setDefaultPageOneReq((prev) => !prev), 1000)
  );

  useEffect(() => {
    setDataSource(allProjects);
  }, [allProjects]);

  const handleSelectOnchange = (value, options, key, setHandler) => {
    if (value.includes("all")) {
      setHandler([...options.map((item) => item.value)]);
      value = options.map((item) => item.value);
    } else if (value.includes("not-all")) {
      setHandler([]);
      value = [];
    } else {
      setHandler(value);
    }
    handleMultiSelect(value, key);
  };

  const handleMultiSelect = (list, key) => {
    setSearch((prevSearch) => {
      const newSearch = { ...prevSearch };

      // If list is empty, remove the key entirely
      if (!list || list.length === 0) {
        if (key === 'status') {
          delete newSearch.projectStatusId;
        } else {
          delete newSearch[key];
        }
      } else {
        if (key === 'status') {
          newSearch.projectStatusId = list;
        } else {
          newSearch[key] = list;
        }
      }

      return newSearch;
    });
  };

  const handleChange = (pagination, filters, sorter) => {
    if (datePickerOpened) {
      return;
    }
    setSortedInfo(sorter);
  };

  const handleDelete = (record) => {
    setCurrentDelRecord(record);
    dispatch(deleteProject(record));
  };

  const searchInput = (key) => (
    <Input
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        setSearch({
          ...search,
          [key]: e.target.value,
        });
        debouncedDefaultPageOneCallApi();
      }}
    />
  );

  const dateInput = (key) => (
    <DatePicker
      placeholder=""
      className="activities-time-picker prod-due-date"
      getPopupContainer={() =>
        document.getElementsByClassName(
          "ant-layout-content layout-content site-layout-background custom-textarea"
        )[0]
      }
      suffixIcon={null}
      format="D MMM YYYY"
      onChange={(e) => {
        setSearch({
          ...search,
          [key]: e,
        });
        debouncedDefaultPageOneCallApi();
      }}
      onOpenChange={(e) => setDatePickerOpened(e)}
    />
  );

  const handleNavigate = (id) =>
    navigate(`/projects-setup/update-project/${id}`);

  const handleDateInput = (value) => {
    setDate(value);
    if (value === null) {
      setSearch({
        ...search,
        startDate: "",
        endDate: "",
      });
    } else {
      setSearch({
        ...search,
        startDate: value[0],
        endDate: value[1],
      });
    }
    debouncedDefaultPageOneCallApi();
  };

  const columns = [
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Project No</div>
            <div>{searchInput("projectNumber")}</div>
          </div>
        );
      },
      dataIndex: "projectNumber",
      key: "projectNumber",
      // sorter: (a, b) => a.projectNumber.localeCompare(b.projectNumber),
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === "projectNumber" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      render: (projectNumber, { id }) => (
        <div onClick={() => handleNavigate(id)}>{projectNumber}</div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Client</div>
            <div>{searchInput("client")}</div>
          </div>
        );
      },
      dataIndex: "client",
      key: "client",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "client" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (client, { id }) => (
        <div onClick={() => handleNavigate(id)}>{client}</div>
      ),
    },
    {
      title: () => {
        return (
          <div
            align="top"
            style={{ width: "100%" }}
            className="select-parent-div"
          >
            <div className="special-table-header-text">Start Date</div>
            <CustomCalendar
              date={date}
              setDate={setDate}
              format={"DD MMM YYYY"}
              handleDateInput={handleDateInput}
              setDatePickerOpened={setDatePickerOpened}
            />
          </div>
        );
      },
      dataIndex: "startDate",
      key: "startDate",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "startDate" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (startDate, { id }) => (
        <div onClick={() => handleNavigate(id)}>
          {moment(startDate).local().format("DD MMM YYYY")}
        </div>
      ),
    },

    {
      title: () => {
        return (
          <div
            align="top"
            style={{ width: "100%" }}
            className="select-parent-div"
          >
            <div className="special-table-header-text">Project Scope</div>
            <div>{searchInput("projectScope")}</div>
          </div>
        );
      },
      dataIndex: "projectScope",
      key: "projectScope",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "projectScope" && sortedInfo?.order,
      ellipsis: true,
      width: "23%",
      render: (projectScope, { id }) => (
        <div
          style={{
            whiteSpace: "break-spaces",
          }}
          onClick={() => handleNavigate(id)}
        >
          {projectScope.length > 180
            ? `${projectScope.substring(0, 180) + " ..."}`
            : projectScope}
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Location</div>
            <div>{searchInput("location")}</div>
          </div>
        );
      },
      dataIndex: "location",
      key: "location",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "location" && sortedInfo?.order,
      ellipsis: true,
      width: "11%",
      render: (locationObject, record) => {
        return (
          <div onClick={() => handleNavigate(record.id)}>
            {locationObject && locationObject.name ? locationObject.name : ""}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Status</div>
            <div style={{ width: "100%" }}>
              <AMultiSelect
                dropdownClassName="status-selector"
                debouncedCallApi={debouncedCallApi}
                disabled={false}
                placeholder=""
                selectTags={statusCheckedList}
                onSelectChange={(value) =>
                  handleSelectOnchange(
                    value,
                    projectStatuses,
                    "status",
                    setStatusCheckedList
                  )
                }
                data={projectStatuses}
                filterOption={(input, option) => {
                  return (
                    String(option?.label)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "statusName",
      key: "statusName",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "statusName" && sortedInfo?.order,
      ellipsis: true,
      width: "8%",
      align: "center",
      render: (status, { projectStatus, id }) => {
        const { statusName, color } = projectStatus;
        return (
          <Tag
            onClick={() => handleNavigate(id)}
            color={color}
            style={{
              color: "#444",
              width: "84px",
              height: "20px",
              textAlign: "center",
              fontSize: "12px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {statusName}
          </Tag>
        );
      },
    },
    {
      title: null,
      dataIndex: "",
      key: "action",
      width: "8%",
      align: "center",
      render: (_, record) => {
        if (!canUpdate && !canDelete) {
          return;
        }
        return deleteProjectLoading && record.id === currentDelRecord.id ? (
          <SpinnerComponent fontSize={14} />
        ) : (
          <Space size={15} align="baseline">
            {canUpdate && (
              <EditIcon
                width={18}
                height={18}
                className="custom-icon"
                onClick={() => {
                  handleNavigate(record.id);
                  dispatch(setProjectDetailsIsEdit(true));
                }}
              />
            )}
            {canDelete && (
              <Popconfirm
                title="Are you sure you want to delete this Project?"
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteIcon width={18} height={18} className="custom-icon" />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Table
        className="special-table project-register-table"
        columns={columns}
        scroll={{ x: !fetchProjectsLoading }}
        onChange={handleChange}
        dataSource={dataSource}
        pagination={false}
        sortUpIcon={<UpOutlined />}
        sortDownIcon={<DownOutlined />}
        loading={fetchProjectsLoading}
      />
      <CustomPagination
        page={page}
        setPage={setPage}
        resultPerPage={resultPerPage}
        setResultPerPage={setResultPerPage}
        debouncedCallApi={debouncedCallApi}
        totalRecord={totalRecords}
      />
    </>
  );
};

export default ProjectsRegisterTable;
